import * as React from "react";
import { Box, Link } from "@chakra-ui/react";

type VideoPlayerProps = {
  url: string;
  aspectRatio?: "16:9" | "4:3";
  title?: string;
};

export function parseEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname;
    const isYouTube = host === "youtube.com" || host.endsWith(".youtube.com");
    const isVimeo = host === "vimeo.com" || host.endsWith(".vimeo.com");

    // youtube.com/watch?v={id}
    if (isYouTube && parsed.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${parsed.searchParams.get("v")}`;
    }

    // youtu.be/{id}
    if (host === "youtu.be") {
      const id = parsed.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }

    // youtube.com/shorts/{id}
    if (isYouTube && parsed.pathname.startsWith("/shorts/")) {
      const id = parsed.pathname.split("/shorts/")[1];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }

    // vimeo.com/{id}
    if (isVimeo) {
      const id = parsed.pathname.slice(1);
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }

    return null;
  } catch {
    return null;
  }
}

export function toExternalUrl(value: string): string {
  const lower = value.toLowerCase();
  if (lower.startsWith("http://") || lower.startsWith("https://")) return value;
  return `https://${value}`;
}

export const VideoPlayer = ({
  url,
  aspectRatio = "16:9",
  title = "School video",
}: VideoPlayerProps): React.ReactElement => {
  const embedUrl = parseEmbedUrl(url);

  if (!embedUrl) {
    return (
      <Link href={toExternalUrl(url)} isExternal>
        {url}
      </Link>
    );
  }

  return (
    <Box
      position="relative"
      width="100%"
      aspectRatio={aspectRatio === "4:3" ? "4/3" : "16/9"}
      borderRadius="md"
      overflow="hidden"
      marginTop={2}
    >
      <Box
        as="iframe"
        src={embedUrl}
        title={title}
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </Box>
  );
};
